import React, { useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Phase 4.16 (L5) Voice-to-Listing.
 *
 * Records audio in the browser via MediaRecorder, uploads the blob to
 * POST /api/listings/voice-to-fields, and calls onResult(fields) with the
 * extracted Listing fields. The parent form can pre-fill the inputs.
 */
export default function VoiceToListingRecorder({ onResult }) {
    const [state, setState] = useState("idle"); // idle | recording | processing
    const [transcript, setTranscript] = useState("");
    const [extracted, setExtracted] = useState(null);
    const [secs, setSecs] = useState(0);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const startRecording = async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error("Mikrofon wird vom Browser nicht unterstützt.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
            const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                await uploadBlob(blob);
            };
            rec.start();
            recorderRef.current = rec;
            setSecs(0);
            timerRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
            setState("recording");
        } catch (e) {
            toast.error("Mikrofon-Zugriff verweigert.");
        }
    };

    const stopRecording = () => {
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") {
            rec.stop();
        }
        stopTimer();
        setState("processing");
    };

    const uploadBlob = async (blob) => {
        if (blob.size < 1000) {
            toast.error("Aufnahme war zu kurz.");
            setState("idle");
            return;
        }
        const fd = new FormData();
        const ext = blob.type.includes("mp4") ? "mp4" : "webm";
        fd.append("audio", blob, `voice.${ext}`);
        try {
            const res = await api.post("/listings/voice-to-fields", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const data = res.data || {};
            setTranscript(data.transcript || "");
            setExtracted(data.fields || {});
            if (data.mocked) {
                toast.message("Voice-to-Listing im Mock-Modus (kein Audio erkannt).");
            } else if (Object.keys(data.fields || {}).length === 0) {
                toast.message("Keine Felder erkannt – bitte Beschreibung präzisieren.");
            } else {
                toast.success("KI hat die Felder vorbereitet – prüfen & übernehmen.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Voice-Upload fehlgeschlagen.");
        } finally {
            setState("idle");
        }
    };

    const applyFields = () => {
        if (extracted && onResult) {
            onResult(extracted);
            toast.success("Felder ins Formular übernommen.");
            setExtracted(null);
            setTranscript("");
        }
    };

    const discard = () => { setExtracted(null); setTranscript(""); };

    const fieldRows = extracted
        ? Object.entries(extracted).filter(([k, v]) => !["model", "confidence"].includes("__") && v != null && v !== "" && !["model"].includes(k))
        : [];

    return (
        <div className="bg-twowheels-yellow/10 border border-twowheels-yellow/40 rounded-md p-4" data-testid="voice-to-listing">
            {/* Feb-2026 (User-Wunsch 24 May Iter 23): Layout neu sortiert —
                VORNE (links): Titel + Aufnehmen-Button auf einer Linie.
                HINTEN (rechts/darunter): kleine Erklärung als feiner Hinweis. */}
            <div className="flex items-start gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-3 shrink-0">
                    {state === "idle" && (
                        <button type="button" onClick={startRecording} data-testid="voice-start-btn"
                            className="group relative bg-twowheels-yellow text-twowheels-yellow-foreground hover:brightness-95 hover:scale-[1.03] hover:shadow-[0_0_0_4px_rgba(255,204,0,0.35)] text-sm font-semibold px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5 transition-all duration-200">
                            {/* Feb-2026 (User-Idee 24 May Iter 23): pulsierender
                                roter Dot als „Live-Aktion"-Signal — verstärkt
                                Aufmerksamkeit ohne den ganzen Button blinken
                                zu lassen. Stoppt bei Hover, damit der Klick
                                klar als bewusste Aktion wahrgenommen wird. */}
                            <span className="relative inline-flex items-center justify-center w-4 h-4">
                                <span className="absolute inset-0 rounded-full bg-red-500/60 animate-ping group-hover:hidden" aria-hidden="true"></span>
                                <Mic className="relative w-4 h-4" />
                            </span>
                            Aufnehmen
                        </button>
                    )}
                    {state === "recording" && (
                        <button type="button" onClick={stopRecording} data-testid="voice-stop-btn"
                            className="bg-red-600 text-white hover:bg-red-700 text-sm font-semibold px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                            <Square className="w-4 h-4" /> Stop ({secs}s)
                        </button>
                    )}
                    {state === "processing" && (
                        <div className="text-sm text-gray-700 inline-flex items-center gap-1.5" data-testid="voice-processing">
                            <Loader2 className="w-4 h-4 animate-spin" /> KI analysiert…
                        </div>
                    )}
                </div>
                <div className="text-sm font-semibold text-gray-900 flex items-start gap-1.5 shrink-0">
                    <Sparkles className="w-4 h-4 text-twowheels-yellow-foreground mt-0.5 shrink-0" />
                    {/* Feb-2026 Iter 28 (User-Wunsch 25 May, Update v4): Zwei Wörter
                        untereinander statt nebeneinander, damit der Hint in der
                        schmalen Spalte sauber bricht. */}
                    <div className="flex flex-col leading-tight">
                        <span>KI-Sprachaufnahme</span>
                        <span>→ Inseratsfelder</span>
                    </div>
                </div>
            </div>

            {transcript && (
                <div className="bg-white border border-gray-200 rounded-sm p-2.5 mb-2" data-testid="voice-transcript">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1">Transkript</div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{transcript}</div>
                </div>
            )}

            {extracted && fieldRows.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-sm p-2.5" data-testid="voice-extracted-fields">
                    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500 mb-1.5">Erkannte Felder</div>
                    <ul className="text-xs text-gray-800 space-y-0.5">
                        {fieldRows.map(([k, v]) => (
                            <li key={k} className="flex gap-2"><span className="font-mono text-gray-500 w-20 shrink-0">{k}:</span> <span className="font-medium">{Array.isArray(v) ? v.join(", ") : String(v)}</span></li>
                        ))}
                    </ul>
                    <div className="flex gap-2 mt-2.5">
                        <button type="button" onClick={applyFields} data-testid="voice-apply-btn"
                            className="bg-green-600 text-white hover:bg-green-700 text-xs font-semibold px-3 py-1.5 rounded-sm">
                            Felder übernehmen
                        </button>
                        <button type="button" onClick={discard} data-testid="voice-discard-btn"
                            className="text-gray-500 hover:text-gray-900 text-xs px-2 py-1.5 inline-flex items-center gap-1">
                            <X className="w-3 h-3" /> Verwerfen
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
